import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  AppDatabaseExport,
  CoachingReport,
  Exercise,
  ExerciseAttempt,
  PuzzleAttempt,
  StoredGame,
} from '../../types';

/**
 * Persistance locale (IndexedDB via `idb`). Propre au navigateur : voir
 * l'avertissement affiché dans l'UI (Mes exercices / Ma progression) et
 * les fonctions d'export/import JSON ci-dessous pour la portabilité.
 */

const DB_NAME = 'magnus';
export const DB_VERSION = 2;

interface MagnusSchema extends DBSchema {
  games: { key: string; value: StoredGame };
  reports: { key: string; value: CoachingReport };
  exercises: { key: string; value: Exercise; indexes: { byGame: string } };
  attempts: { key: string; value: ExerciseAttempt; indexes: { byExercise: string } };
  puzzleAttempts: { key: string; value: PuzzleAttempt; indexes: { byPuzzle: string } };
}

let dbPromise: Promise<IDBPDatabase<MagnusSchema>> | null = null;

export function getDb(): Promise<IDBPDatabase<MagnusSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<MagnusSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('games')) {
          db.createObjectStore('games', { keyPath: 'game.id' });
        }
        if (!db.objectStoreNames.contains('reports')) {
          db.createObjectStore('reports', { keyPath: 'gameId' });
        }
        if (!db.objectStoreNames.contains('exercises')) {
          const store = db.createObjectStore('exercises', { keyPath: 'id' });
          store.createIndex('byGame', 'gameId');
        }
        if (!db.objectStoreNames.contains('attempts')) {
          const store = db.createObjectStore('attempts', { keyPath: 'id' });
          store.createIndex('byExercise', 'exerciseId');
        }
        // v2 : ajoutée pour la banque de puzzles hors-ligne — un store de plus,
        // aucune migration nécessaire sur les stores existants.
        if (!db.objectStoreNames.contains('puzzleAttempts')) {
          const store = db.createObjectStore('puzzleAttempts', { keyPath: 'id' });
          store.createIndex('byPuzzle', 'puzzleId');
        }
      },
    });
  }
  return dbPromise;
}

/** Réservé aux tests : force une nouvelle instance (utile avec fake-indexeddb entre les tests). */
export function resetDbHandleForTests() {
  dbPromise = null;
}

// ---- Parties ----

/** Enregistre une partie si elle n'existe pas déjà (dédoublonnage par id = hash du PGN normalisé). */
export async function saveGameIfNew(stored: StoredGame): Promise<{ inserted: boolean }> {
  const db = await getDb();
  const existing = await db.get('games', stored.game.id);
  if (existing) return { inserted: false };
  await db.put('games', stored);
  return { inserted: true };
}

/** Comme `saveGameIfNew`, mais fusionne les métadonnées (ex: `playerColor`,
 * `chessCom`) sur une partie déjà connue plutôt que de l'ignorer — utilisé
 * par la synchro Chess.com et par la saisie tardive de la couleur jouée.
 * Conserve toujours `importedAt` d'origine. */
export async function upsertGame(stored: StoredGame): Promise<{ inserted: boolean }> {
  const db = await getDb();
  const existing = await db.get('games', stored.game.id);
  await db.put('games', existing ? { ...existing, ...stored, importedAt: existing.importedAt } : stored);
  return { inserted: !existing };
}

export async function listGames(): Promise<StoredGame[]> {
  const db = await getDb();
  return db.getAll('games');
}

export async function getGame(id: string): Promise<StoredGame | undefined> {
  const db = await getDb();
  return db.get('games', id);
}

export async function deleteGame(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('games', id);
}

// ---- Rapports d'analyse ----

export async function saveReport(report: CoachingReport): Promise<void> {
  const db = await getDb();
  await db.put('reports', report);
}

export async function getReport(gameId: string): Promise<CoachingReport | undefined> {
  const db = await getDb();
  return db.get('reports', gameId);
}

// ---- Exercices ----

/** put() : ré-analyser la même partie régénère les mêmes ids d'exercices (dérivés du moment), donc pas de doublon. */
export async function saveExercises(exercises: Exercise[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('exercises', 'readwrite');
  await Promise.all(exercises.map((e) => tx.store.put(e)));
  await tx.done;
}

export async function listExercises(): Promise<Exercise[]> {
  const db = await getDb();
  return db.getAll('exercises');
}

export async function getExercisesByGame(gameId: string): Promise<Exercise[]> {
  const db = await getDb();
  return db.getAllFromIndex('exercises', 'byGame', gameId);
}

export async function updateExercise(exercise: Exercise): Promise<void> {
  const db = await getDb();
  await db.put('exercises', exercise);
}

// ---- Tentatives ----

export async function saveAttempt(attempt: ExerciseAttempt): Promise<void> {
  const db = await getDb();
  await db.put('attempts', attempt);
}

export async function listAttemptsForExercise(exerciseId: string): Promise<ExerciseAttempt[]> {
  const db = await getDb();
  return db.getAllFromIndex('attempts', 'byExercise', exerciseId);
}

export async function listAllAttempts(): Promise<ExerciseAttempt[]> {
  const db = await getDb();
  return db.getAll('attempts');
}

// ---- Tentatives de puzzles ----

export async function savePuzzleAttempt(attempt: PuzzleAttempt): Promise<void> {
  const db = await getDb();
  await db.put('puzzleAttempts', attempt);
}

export async function listPuzzleAttempts(): Promise<PuzzleAttempt[]> {
  const db = await getDb();
  return db.getAll('puzzleAttempts');
}

// ---- Export / import JSON ----

export async function exportDatabase(): Promise<AppDatabaseExport> {
  const db = await getDb();
  const [games, reports, exercises, attempts, puzzleAttempts] = await Promise.all([
    db.getAll('games'),
    db.getAll('reports'),
    db.getAll('exercises'),
    db.getAll('attempts'),
    db.getAll('puzzleAttempts'),
  ]);
  return { version: DB_VERSION, exportedAt: new Date().toISOString(), games, reports, exercises, attempts, puzzleAttempts };
}

/** Importe une sauvegarde JSON. Fusionne sans dupliquer (upsert par id).
 * Accepte aussi bien les sauvegardes v1 (sans `puzzleAttempts`) que v2. */
export async function importDatabase(data: AppDatabaseExport): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['games', 'reports', 'exercises', 'attempts', 'puzzleAttempts'], 'readwrite');
  await Promise.all([
    ...data.games.map((g) => tx.objectStore('games').put(g)),
    ...data.reports.map((r) => tx.objectStore('reports').put(r)),
    ...data.exercises.map((e) => tx.objectStore('exercises').put(e)),
    ...data.attempts.map((a) => tx.objectStore('attempts').put(a)),
    ...(data.puzzleAttempts ?? []).map((p) => tx.objectStore('puzzleAttempts').put(p)),
  ]);
  await tx.done;
}
